import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  alpha,
} from '@mui/material';
import { Community } from '../../types/community';

interface RulesTabProps {
  community: Community & { rules?: string[] };
  user: any;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
}

const RulesTab: React.FC<RulesTabProps> = ({
  community,
  user,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
}) => {
  // Ensure rules exists
  const rules = community.rules || [];

  return (
    <Card sx={{ 
      p: { xs: 2, md: 4 },
      borderRadius: '20px',
      ...glassEffect,
      border: `1px solid ${alpha(goldColor, 0.2)}`,
      minHeight: { xs: 'auto', md: '400px' }
    }}>
      <Typography variant="h5" sx={{ 
        mb: 3, 
        fontWeight: 800, 
        color: isDark ? '#fff' : deepGreen,
        fontSize: { xs: '1.2rem', md: '1.5rem' }
      }}>
        Community Rules
      </Typography>
      
      {rules.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rules.map((rule: string, index: number) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 1.5, md: 2 },
                p: { xs: 2, md: 2.5 },
                borderRadius: '12px',
                bgcolor: isDark ? alpha('#fff', 0.05) : alpha(deepGreen, 0.03),
                border: `1px solid ${alpha(goldColor, 0.1)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateX(4px)',
                  borderColor: goldColor
                }
              }}
            >
              <Box
                sx={{
                  width: { xs: 24, md: 28 },
                  height: { xs: 24, md: 28 },
                  borderRadius: '50%',
                  bgcolor: goldColor,
                  color: deepGreen,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  flexShrink: 0,
                  fontSize: { xs: '0.8rem', md: '0.9rem' }
                }}
              >
                {index + 1}
              </Box>
              <Typography sx={{ 
                lineHeight: 1.6,
                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                fontSize: { xs: '0.85rem', md: '1rem' }
              }}>
                {rule}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: goldColor, mb: 2, opacity: 0.8, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            📜
          </Typography>
          <Typography sx={{ 
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', 
            mb: 2,
            fontSize: { xs: '0.9rem', md: '1rem' }
          }}>
            No rules have been set for this community
          </Typography>
          {(community.userRole === 'owner' || community.userRole === 'moderator') && user && (
            <Button
              variant="outlined"
              sx={{
                borderColor: goldColor,
                color: goldColor,
                fontWeight: 600,
                borderRadius: '12px',
                px: 3,
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.1),
                }
              }}
            >
              Add Rules
            </Button>
          )}
        </Box>
      )}
    </Card>
  );
};

export default RulesTab;